/**
 * 이상한 방 - 봉투 화면 (순수 DOM 연출)
 *
 * 근거 문서: 로드맵 확정안 3.2절(봉투) · 3.3절(연출 시퀀스) · 4절 8·9번(하드 피티 · 정직한 바늘)
 *           6절("근접 실패 색 깜빡" 기각 - 바늘은 실제 값) · `ui/juice.ts` 머리말(정리 권한은 타이머 하나 · `Math.random` 0)
 *
 * ## 이 화면은 그린다. 뽑지 않는다.
 *
 * 무엇이 나왔는가는 `game/economy/envelope.ts`가 이미 정했고(`DrawResult`), 그것이 계정에 무엇을 남겼는가는
 * `inventory.applyReward`가 정했다. 이 파일은 그 둘을 받아 보여 줄 뿐이며 **난수를 하나도 만들지 않는다** -
 * 파편의 자리·각도조차 뽑기 결과에서 파생한다(`envelopeCeremony.shardsFor`).
 * 바늘이 멎는 자리는 `DrawResult.gradeRoll` 그 실수값이다 - SR 경계 옆에 멎는 근접 실패는 연출이 아니라 사실이고,
 * 그래서 이 화면에는 "아깝게 빗나간 척"을 만드는 코드가 없다(로드맵 6절이 기각했다).
 *
 * ## 시퀀스 - 개봉 전은 같고, 개봉 후는 등급이 정한다
 *
 * | 시각 | 무엇 |
 * |---|---|
 * | 0~360 | **앞당김.** 봉투가 아래에서 올라와 먹색 플레이트에 자리 잡고 봉인이 달아오른다. 핀 칩에서 값이 떨어진다 |
 * | 360~2960 | **릴.** 실제 폭(60 / 30 / 9 / 1)의 띠를 여러 번 이어 붙인 트랙이 흐르다 감속해 `gradeRoll`에 멎는다. 지나는 경계마다 똑딱이 울리고 간격이 벌어지며, 마지막 접근에 차오르는 소리가 반음씩 올라 정지와 함께 끝난다 |
 * | 2960 | **착지.** 마커가 한 번 밝아지고 똑딱 하나가 겹친다. 시각도 빛도 등급과 무관하다 |
 * | 2960~3280 | **숨 한 박자 정지.** 떨림도 소리도 멎는다. 0.32초의 침묵이 긴장의 핵심이다 |
 * | 3280~ | 등급이 드러난다. 여기서부터의 길이·빛·파편은 `GRADE_CEREMONY`가 정한다 - N 0.35초(툭 열리고 끝) ~ SSR 2.84초(어두워짐 → 금빛 → 링·빛기둥 → 연쇄 플래시 + 조각 56 + 남는 오라) |
 *
 * N이 약 3.6초, SSR이 약 6.1초다. 전에는 1.75 / 3.6초였고, 사람은 그걸 두고 "누르자마자 뾱 뽑힌다"고 했다 -
 * 짧았던 것은 뒷부분이 아니라 **앞부분**이었다(`envelopePhases.ts` 머리말). SSR 쪽만 5.5초에서 6.1초로 한 번 더
 * 늘어난 것은 P5다 - 위 둘의 연출이 커졌고 **N·R의 값은 한 자리도 안 움직였다**(그 대비가 규칙이다).
 *
 * **개봉 전 3.28초는 네 등급이 글자 하나까지 같다.** 길이도 소리도 떨림도 같고, 등급 색·등급 글자는 DOM에 없다 -
 * 흘리면 긴장이 아니라 스포일러이고, 흘리는 척만 하는 것은 6절이 기각한 근접 실패 속임수다. 화려함은 예고가 아니라 보상이다.
 *
 * 열 장은 낮은 등급부터 **한 장씩** 열린다(P5). 아홉 장도 큰 카드 자리에서 제 등급의 세리머니를 받고, 끝난 장이
 * 미니 칩으로 내려앉는다 - 최고 등급은 마지막에 풀 연출이다(리빌의 "마지막이 가장 크게"). 그만큼 길어지므로
 * 건너뛰기가 늘 서 있다: 화면 아무 데나 탭하거나 머리 줄의 [건너뛰기]를 누르면 어느 시각에서든 곧바로 카드까지 간다 -
 * 아는 사람은 기다릴 이유가 없다. `prefers-reduced-motion`이면 스윕도 세리머니도 없이 컷이다.
 *
 * ## 정리의 권한은 타이머 하나다
 *
 * `juice.ts`와 같은 규약이다. 단계 사이의 예약은 언제나 하나뿐이고(`schedule`이 앞의 것을 지운다),
 * `animationend`·`transitionend`를 듣지 않는다 - 움직임 줄이기에서는 그 이벤트가 오지 않고, 그러면
 * 카드가 영영 뒤집히지 않는다. 화면을 접는 것(`hide`)도 그 타이머를 걷는 것으로 끝난다. 그 타이머와 시각표는
 * `envelopePhases.ts`에 산다.
 *
 * ## 문구도 확률도 이 파일에 없다
 *
 * 문구는 `i18n`, 확률 문자열은 `envelopeTable.gradeOddsLabel`에서 온다. 여기서 `'60%'`를 적으면 표를 고친 날
 * 화면이 거짓말을 하고, 확률 표시 의무가 있는 지역에서는 그것이 곧 위반이다.
 *
 * ## 아홉으로 갈렸다 (7절 M3 기록이 예고한 이음매)
 *
 * 이 파일이 693줄이 되어 "언제"와 화면의 영역마다 한 조각씩 옆으로 냈다. 이 파일은 조각을 조립하고 입력(탭·키)을
 * 받아 넘기는 입구다. **예약(`setTimeout`)은 `envelopePhases.ts` 한 곳에만 있고**, 나머지는 "무엇을 어떻게
 * 그리는가"만 안다. 시각표는 둘이되 자리가 갈려 있다 - 개봉 전은 `envelopePhases`, 개봉 후는 `envelopeCeremony`.
 *
 *  - `envelopePhases.ts`   `EnvelopeView` · 단계 전이 · 예약 슬롯 둘(단계 · 소리)
 *  - `envelopeTimeline.ts`  `ENVELOPE_TIMELINE`(개봉 전 시각표)와 거기서 곧바로 나오는 두 길이
 *  - `envelopeCeremony.ts` `GRADE_CEREMONY`(개봉 후) · 열 장의 한 장 길이(`stepRevealMs`) · 파편 파생 · 빛/어둠 레이어
 *  - `envelopeHead.ts`     눈썹 라벨·제목·핀 칩·탄창 칩 - 계정의 수를 받은 그대로 놓는다
 *  - `envelopeGauge.ts`    확률 띠·바늘·범례·피티 게이지 + 릴의 DOM - 거짓말이 가능한 값이 전부 거기 모여 "값을 만들지 않는다"
 *  - `envelopeReel.ts`     돌아가는 릴의 산술(바퀴 수·위치·똑딱 시각). 순수 리프라 DOM도 시계도 없다
 *  - `envelopeStack.ts`    열 장의 낮은 등급 아홉 장. `revealOrder`의 원본
 *  - `envelopeCard.ts`     봉투·봉인·카드 한 장. `EnvelopeCardView` · `rewardLabel` · `punchStepFor`의 원본
 *  - `envelopeActions.ts`  [한 장 더] [열 장] [닫기] - 끝난 뒤에만 보이고, 잠긴 버튼은 이유를 말한다
 *
 * 테스트와 사물함이 부르던 이름(`ENVELOPE_TIMELINE` `rewardLabel` 등)은 여기서 다시 내보낸다 - 원본은 한 곳이고
 * 이 파일은 봉투 화면의 입구다.
 */

import type { EnvelopeSoundName } from '../audio/sounds'
import { onLocaleChange } from '../i18n'

import { createElement } from './dom'
import { createEnvelopeActions } from './envelopeActions'
import { createEnvelopeCard } from './envelopeCard'
import { createEnvelopeCeremony } from './envelopeCeremony'
import { createEnvelopeGauge } from './envelopeGauge'
import { createEnvelopeHead } from './envelopeHead'
import { createEnvelopePhases, type EnvelopeView } from './envelopePhases'
import { createEnvelopeStack } from './envelopeStack'
import { isSettingsOpen } from './settings'

import './plateScreen.css'
import './envelope.css'

export { punchStepFor, rewardLabel, type EnvelopeCardView } from './envelopeCard'
export {
  GRADE_CEREMONY,
  REDUCED_CEREMONY,
  ceremonyFor,
  ceremonyTotalMs,
  shardsFor,
  stepRevealMs,
  tenStackTotalMs,
  type GradeCeremony,
} from './envelopeCeremony'
export { type EnvelopeView } from './envelopePhases'
export { ENVELOPE_TIMELINE, envelopeDurationMs, reelSpinMs } from './envelopeTimeline'
/*
 * 릴에서 밖으로 나가는 것은 아래 여섯뿐이다. 배럴은 화면의 입구라 여기 적힌 이름이 곧 "밖에서 불러도 된다"는
 * 뜻이 되고, 산술 리프의 내부(`reelEase`·`reelPosition`·`reelTickTimes`·`reelTurnsFor`·`reelSoundPlan`)까지
 * 그렇게 새면 리프를 고칠 때 무엇이 계약인지가 흐려진다. 그 안쪽을 보는 것은 자기 테스트뿐이고,
 * 그 테스트는 `./envelopeReel`을 직접 부른다.
 */
export {
  REEL,
  REEL_EASE_CSS,
  REEL_GRAIN_CSS,
  reelRunFor,
  reelShift,
  reelSpinPeriodMs,
} from './envelopeReel'
export { revealOrder } from './envelopeStack'

/* --- 타입 ---------------------------------------------------------------------- */

export type EnvelopeHandlers = Readonly<{
  /** [한 장 더] 또는 [열 장]. 뽑기·결제·저장은 받는 쪽의 일이고 끝나면 `show`를 다시 부른다. */
  onOpen: (count: 1 | 10) => void
  onClose: () => void
}>

export type EnvelopeScreenHandle = Readonly<{
  show: (view: EnvelopeView) => void
  hide: () => void
  isOpen: () => boolean
  dispose: () => void
}>

export type EnvelopeScreenOptions = Readonly<{
  mount: HTMLElement
  handlers: EnvelopeHandlers
  /**
   * 소리. 재생은 엔진의 몫이라 이름만 넘긴다(`sessionAudio.ts`의 `SoundSink`와 같은 결정) -
   * 이 화면이 엔진을 직접 잡으면 테스트가 오디오 없이 돌지 못하고, 음소거 규칙이 두 곳에 생긴다.
   */
  playSound?: ((name: EnvelopeSoundName) => void) | undefined
}>

/* --- 본체 ---------------------------------------------------------------------- */

/**
 * 봉투 화면을 만들어 `mount`에 붙인다. 처음에는 숨겨져 있다. 단계 선택 화면이 부른다 -
 * 결과 화면에는 여는 버튼이 없다(그 화면의 유일한 목적은 "한 판 더"다).
 */
export function createEnvelopeScreen(options: EnvelopeScreenOptions): EnvelopeScreenHandle {
  const root = createElement('div', 'ev-root')
  root.hidden = true
  root.setAttribute('role', 'dialog')
  root.setAttribute('aria-modal', 'true')
  root.setAttribute('aria-labelledby', 'ev-title')

  /* 크롭마크 넷. SR 이상에서 한 번 노랑으로 켜진다(`gameHud.css`의 `.gh-crop`과 같은 어휘). */
  const crops = createElement('div', 'ev-crops')
  crops.setAttribute('aria-hidden', 'true')
  for (const corner of ['tl', 'tr', 'bl', 'br']) crops.append(createElement('span', `ev-crop ev-crop--${corner}`))

  /* --- 영역마다 한 파일 (머리 · 게이지·피티 · 열 장 스택 · 봉투와 카드 · 버튼 줄) --- */
  const head = createEnvelopeHead()
  const gauge = createEnvelopeGauge()
  const stack = createEnvelopeStack()
  const card = createEnvelopeCard()
  const hint = createElement('p', 'ev-hint', '')
  const actions = createEnvelopeActions(options.handlers)

  /*
   * 건너뛰기 버튼. 화면 어디를 눌러도 건너뛰는 것은 **그대로 두고**(아는 사람은 이미 그렇게 한다) 그 문을
   * 눈에 보이게 만든 것이다 - 열 장이 한 장씩 열리게 되면서 기다리지 않기로 한 사람의 길이 글자 한 줄
   * (`.ev-hint`)뿐이면 안 된다. 머리 줄 오른쪽 끝에 서고 좁은 화면에서도 제목과 겹치지 않는다(격자의 제 칸이다).
   */
  const skip = createElement('button', 'ev-skip', '')
  skip.type = 'button'
  head.root.append(skip)

  /*
   * 세리머니 레이어(빛·조각·어둠)는 **맨 마지막 자식**이다. 카드 위에 그려져야 조각이 카드 앞으로 흩날리고,
   * SR·SSR의 어둠이 카드까지 덮는다. 빛의 한가운데는 비어 있어 카드를 가리지 않는다(`envelopeCeremony.css`).
   */
  const ceremony = createEnvelopeCeremony(root, card.root)

  const body = createElement('div', 'ev-body')
  body.append(gauge.root, gauge.pityRoot, stack.root, card.root, hint)
  root.append(crops, head.root, body, actions.root, ceremony.root)
  options.mount.append(root)

  /* --- "언제"는 한 곳 --- */
  const phases = createEnvelopePhases({
    root,
    crops,
    hint,
    skip,
    head,
    gauge,
    stack,
    card,
    ceremony,
    actions,
    play: (name) => options.playSound?.(name),
  })

  /* --- 입력 --- */

  /*
   * 화면 아무 데나 눌러도 건너뛴다. 다만 **건너뛰기 버튼 안에서 시작한 손짓은 여기서 보지 않는다** -
   * 버튼은 `click`으로 받기 때문이다(`onSkip`). 같은 손짓으로 `pointerdown`과 `click`이 잇달아 오므로
   * 둘 다 받으면 `jumpToDone`이 두 번 불린다. 무시하는 쪽을 루트에 둔 이유는 버튼이 `pointerdown`을
   * 막아 버리면 키보드(Enter·Space)로는 버튼이 눌리지 않기 때문이다 - 그 입력은 `click`으로만 온다.
   */
  function onTap(event: PointerEvent): void {
    const phase = phases.current()
    if (root.hidden || phase === 'done' || phase === 'idle') return
    if (!event.isPrimary) return
    if (event.target instanceof Node && skip.contains(event.target)) return
    phases.jumpToDone()
  }

  /** 건너뛰기 버튼. 마우스·손가락·키보드가 전부 `click` 하나로 들어온다. */
  function onSkip(): void {
    if (root.hidden || phases.current() === 'idle') return
    phases.jumpToDone()
  }

  function handleKeyDown(event: KeyboardEvent): void {
    if (root.hidden || isSettingsOpen()) return
    if (event.altKey || event.ctrlKey || event.metaKey) return
    if (event.key === 'Escape') {
      event.preventDefault()
      options.handlers.onClose()
      return
    }
    if (event.key !== 'Enter' && event.key !== ' ') return
    if (document.activeElement instanceof HTMLButtonElement) return
    event.preventDefault()
    if (phases.current() !== 'done') {
      phases.jumpToDone()
      return
    }
    // 끝난 뒤의 Enter는 큰 버튼이다. 그 버튼이 잠겨 있으면 닫기다 - 아무 일도 안 하는 Enter는 고장으로 읽힌다.
    if (actions.canOpenOne()) options.handlers.onOpen(1)
    else options.handlers.onClose()
  }

  root.addEventListener('pointerdown', onTap)
  skip.addEventListener('click', onSkip)
  window.addEventListener('keydown', handleKeyDown)

  const stopLocaleWatch = onLocaleChange(() => {
    if (phases.hasView()) phases.paintTexts()
  })

  phases.paintTexts()

  return {
    show(view: EnvelopeView): void {
      // 드러내는 것이 먼저다 - 컷(빈 뷰 · 움직임 줄이기)은 곧바로 버튼에 포커스를 주는데, 감춰진 노드는 포커스를 받지 않는다.
      root.hidden = false
      phases.show(view)
    },

    hide(): void {
      phases.stop()
      if (root.contains(document.activeElement)) {
        const focused = document.activeElement
        if (focused instanceof HTMLElement) focused.blur()
      }
      root.hidden = true
    },

    isOpen: () => !root.hidden,

    dispose(): void {
      phases.stop()
      stopLocaleWatch()
      head.dispose()
      actions.dispose()
      root.removeEventListener('pointerdown', onTap)
      skip.removeEventListener('click', onSkip)
      window.removeEventListener('keydown', handleKeyDown)
      root.remove()
    },
  }
}
